'use client'

import { ArticleDto } from '@/shared/api/strapi/requests/home'
import { useCallback, useEffect, useRef, useState } from 'react'
import { EmblaCarouselType } from 'embla-carousel'
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  SliderDots,
} from '@/shared/ui/carousel'
import Autoplay from 'embla-carousel-autoplay'
import { cn, formatDate } from '@/shared/lib/utils'
import { Link } from '@/shared/i18n'
import { ImageFill } from '@/shared/ui/image-fill'

const AUTOPLAY_DELAY = 4000
const AFTER_INTERACTION_DELAY = 6000

export const NewsCarousel = ({ news }: { news: ArticleDto[] }) => {
  const items = news.length > 1 ? news.concat(news) : news
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [emblaThumbsApi, setEmblaThumbsApi] = useState<EmblaCarouselType>()
  const [interacted, setInteracted] = useState(false)
  const timeoutIdRef = useRef<number>(0)

  const handleInteraction = () => {
    setInteracted(true)
    const { current: prevId } = timeoutIdRef
    clearTimeout(prevId)
    timeoutIdRef.current = setTimeout(() => {
      setInteracted(false)
    }, AFTER_INTERACTION_DELAY) as never
  }

  useEffect(() => {
    if (!emblaThumbsApi) return
    emblaThumbsApi.scrollTo(selectedIndex)
  }, [emblaThumbsApi, selectedIndex])

  const onThumbSelect = useCallback(() => {
    if (!emblaThumbsApi) return
    setSelectedIndex(emblaThumbsApi.selectedScrollSnap())
  }, [emblaThumbsApi, setSelectedIndex])

  useEffect(() => {
    if (!emblaThumbsApi) return
    emblaThumbsApi.on('select', onThumbSelect).on('reInit', onThumbSelect)
    return () => {
      emblaThumbsApi.off('select', onThumbSelect).on('reInit', onThumbSelect)
    }
  }, [emblaThumbsApi, onThumbSelect])
  return (
    <div
      className={`flex flex-col w-full gap-2}`}
      onMouseEnter={handleInteraction}
      onMouseMove={handleInteraction}
      onClick={handleInteraction}
      onFocus={handleInteraction}
      onTouchStart={handleInteraction}
      onTouchMove={handleInteraction}
    >
      <Carousel
        plugins={[
          Autoplay({
            delay: AUTOPLAY_DELAY,
            active: !interacted,
          }),
        ]}
        className="w-full flex flex-col gap-2"
        opts={{ loop: true, align: 'start' }}
        setApi={setEmblaThumbsApi}
      >
        <CarouselContent className={'w-full'}>
          {items.map((item, index) => {
            const { attributes, id } = item
            const { image, title, tags, publicated_at, slug } = attributes
            const { url, alternativeText } = image.data.attributes
            return (
              <CarouselItem
                key={`${id}${index}`}
                className={cn(
                  `group w-full select-none`,
                  news.length === 2 && 'sm:w-2/3 sm:basis-2/3',
                  news.length >= 3 && 'sm:w-2/3 sm:basis-2/3 lg:w-2/5 lg:basis-2/5',
                )}
              >
                <Link
                  href={`/news/${slug}`}
                  className={'h-full w-full flex flex-col gap-[10px] lg:gap-[20px] 2xl:gap-[23px]'}
                >
                  <div className={cn('h-[200] sm:h-[290] relative')}>
                    <ImageFill
                      src={url}
                      alt={alternativeText || ''}
                      className={cn(
                        'absolute inset-x-0 bottom-0 top-0',
                        'rounded-xl',
                        'object-cover h-full w-full',
                        'transition-all duration-300',
                        index === selectedIndex ? 'opacity-100' : 'opacity-50',
                        'group-hover:opacity-100 hover:translate-y-0',
                      )}
                    />
                  </div>
                  <div className={'flex flex-col gap-2'}>
                    <p className="text-gray-400 text-base lg:text-xl mb-[5px] lg:mb-[7px] font-normal">
                      {formatDate(publicated_at)}
                    </p>
                    <p className="text-sm lg:text-xl mb-[5px] lg:mb-[7px] font-normal">{title}</p>
                    <p className="text-gray-400 text-sm font-normal">
                      {tags.data.map(tag => `#${tag.attributes.name}`).join(' ')}
                    </p>
                  </div>
                </Link>
              </CarouselItem>
            )
          })}
        </CarouselContent>
        <div className={'w-full flex flex-row items-center'}>
          <SliderDots className={'grow'} activeIndex={selectedIndex % news.length} total={news.length} />
          <div className={'flex flex-row items-center justify-start self-end'}>
            <CarouselPrevious absolute={false} className={'hidden sm:inline-flex'} />
            <CarouselNext absolute={false} className={'hidden sm:inline-flex'} />
          </div>
        </div>
      </Carousel>
    </div>
  )
}
